import React, { createContext, useState, useEffect, useMemo } from 'react';

import { useLocation } from 'react-router-dom';

import { PostTransportAndJourney, PostAdditionalDetails } from '../../api/GBENSDec';
import { getErrorsDetails } from '../../api/dashboard';
import Stepper from '../../components/Stepper';
import { is360PayStaff } from '../../utils/roles';
import { READ_ONLY_LABEL } from '../../utils/constants';

import dayjs from 'dayjs';

//Adapter JS Imports
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

//--------------------Material UI Imports----------------------
import { Tabs, Tab, Container } from '@mui/material';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import WarningIcon from '@mui/icons-material/Warning';

//--------------------Screen Imports----------------------
import TransportAndJourney from './Screens/1. TransportAndJourney';
import Consignments from './Screens/2. Consignments';
import AddingConsignment from './Screens/2.1. Adding Consignment';
import UpdatingConsignments from './Screens/2.2. Updating Consignment';
import ConsignmentUpload from './Screens/2.3 Consignment Upload';
import AdditionalDetails from './Screens/3. Additional Details';
import ReviewAndSubmit from './Screens/4. Review and Submit';

import FixOrContinueModal from './Modals/FixOrContinueModal';
import ReviewAndSubmitMenu from './ReviewAndSubmitMenu';

//--------------------Hook Imports----------------------
import useErrors from './Hooks/useErrors';
import useData from './Hooks/useData';
import useConsignment from './Hooks/useConsignment';
import useReferenceData from './Hooks/useReferenceData';
import LoadingSpinner from '../../components/LoadingSpinner';

export const GBENSContext = createContext(null);

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}
const GBENSContainer = () => {
  const { state } = useLocation();

  const [headerId, setHeaderId] = useState(null);
  const [companyId, setCompanyId] = useState(null);

  const [navToConsignmentId, setNavToConsignmentId] = useState(null);

  //--------------------HMRC Error Dialog----------------------
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogOpenForInProgress, setErrorDialogOpenForInProgress] = useState(false);
  const [menuErrorValue, setMenuErrorValue] = useState(0);
  const [hmrcErrorData, setHMRCErrorData] = useState([]);

  //--------------------Modal state stored in container to allow access in multiple places----------------------
  const [deleteConsignmentModalOpen, setDeleteConsignmentModalOpen] = useState(false);
  const [deleteConsignmentRowsSelected, setDeleteConsignmentRowsSelected] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [deleteShipmentModalOpen, setDeleteShipmentModalOpen] = useState(false);
  const [cannotDeleteShipmentModalOpen, setCannotDeleteShipmentModalOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  //--------------------Required here to avoid circular dependency----------------------
  const [FixOrContinueModalOpen, setFixOrContinueModalOpen] = useState(false);
  const [toRunInitialErrorCheck, setToRunInitialErrorCheck] = useState(false);
  const [goToNextStepFunc, setGoToNextStepFunc] = useState(false);
  const [activeStep, setActiveStep] = useState(null);
  const [errorsChecked, setErrorsChecked] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const [consignmentsVisited, setConsignmentsVisited] = useState(false);
  const [currentConsignmentErrors, setCurrentConsignmentErrors] = useState(null);
  const [currentConsignmentHMRCErrors, setCurrentConsignmentHMRCErrors] = useState(null);
  const [currentConsignment, setCurrentConsignment] = useState(null);

  //--------------------Data----------------------
  const { data, setData, updateData, moveData, deleteData } = useData(
    state,
    setHeaderId,
    setCompanyId,
    setToRunInitialErrorCheck
  );
  //--------------------Reference Data----------------------
  const { referenceData, refreshNeenors } = useReferenceData(companyId);

  //--------------------Errors----------------------
  const {
    errors,
    setErrors,
    errorsLocal,
    setErrorsLocal,
    errorsHMRC,
    setErrorsHMRC,
    checkErrors,
    checkErrorsStandard,
    checkErrorsConsignment,
    checkErrorsConsignmentLocal,
    hasErrors,
    handleShowWarningModal,
    allValid,
    isAccompanied,
    tabValidationErrors,
    HMRCtabValidationErrors,
    currentConsignmentHasData,
    initialErrorCheckRun,
  } = useErrors(
    state,
    referenceData,
    toRunInitialErrorCheck,
    setToRunInitialErrorCheck,
    setGoToNextStepFunc,
    setActiveStep,
    errorsChecked,
    setErrorsChecked,
    consignmentsVisited,
    currentConsignmentErrors,
    setCurrentConsignmentErrors,
    currentConsignmentHMRCErrors,
    setCurrentConsignmentHMRCErrors,
    currentConsignment,
    data
  );

  //--------------------Consignment----------------------
  const {
    addNewConsignment,
    editConsignment,
    updateCurrentConsignment,
    saveCurrentConsignment,
    moveConsignmentData,
    setBlankConsignmentData,
    selectNeenor,
    deleteConsignmentData,
    addNewConsignmentLine,
    updateCurrentLine,
    editConsignmentLine,
    saveConsignmentLine,
    deleteConsignmentLine,
    deleteConsignment,
    hasConsignments,
    currentLine,
    handleGetMRNDetails,
  } = useConsignment(
    currentConsignment,
    setCurrentConsignment,
    currentConsignmentErrors,
    setCurrentConsignmentErrors,
    currentConsignmentHMRCErrors,
    setCurrentConsignmentHMRCErrors,
    data,
    setData,
    errors,
    setErrors,
    errorsHMRC,
    setErrorsHMRC,
    headerId,
    refreshNeenors
  );

  //--------------------Navigate to consignment from dashboard----------------------
  useEffect(() => {
    if (state?.consignmentId && navToConsignmentId === null && initialErrorCheckRun) {
      setNavToConsignmentId(state.consignmentId);
    }
  }, [state, initialErrorCheckRun]);

  //--------------------Check if fields should be readonly ------------------------------
  useEffect(() => {
    setIsReadOnly(state.isReadOnly || is360PayStaff());
  }, [state.isReadOnly]);

  //--------------------Check for errors before moving to next step----------------------
  useEffect(() => {
    if (goToNextStepFunc && errorsChecked) {
      if (hasErrors(activeStep) && (activeStep !== 'AddingConsignment' || currentConsignmentHasData))
        setFixOrContinueModalOpen(true);
      else if (
        activeStep === 'uploadConsignment' ||
        activeStep === 'ReviewAndSubmit' ||
        !hasErrors(activeStep) ||
        (hasErrors(activeStep) && activeStep === 'AddingConsignment' && !currentConsignmentHasData)
      ) {
        onContinue();
      }
      setErrorsChecked(false);
    }
  }, [goToNextStepFunc, errorsChecked, hasErrors, activeStep]);

  //--------------------Save data before moving to next step----------------------
  const onContinueTransportAndJourney = async () => {
    let transportData = { ...data['TransportAndJourney'] };

    if (transportData.arrivalDateTime)
      transportData.arrivalDateTime = dayjs(transportData.arrivalDateTime).format('YYYY-MM-DDTHH:mm:ss.SSS');

    if (transportData.departureDate)
      transportData.departureDate = dayjs(transportData.departureDate).format('YYYY-MM-DDTHH:mm:ss.SSS');

    let dt;
    if (!headerId) {
      dt = await PostTransportAndJourney({ TransportAndJourney: transportData }, companyId);
    } else {
      dt = await PostTransportAndJourney({ TransportAndJourney: { headerId, ...transportData } }, companyId);
    }

    if (dt?.[0]?.headerId) {
      setHeaderId(dt[0].headerId);
    } else if (dt?.[0]?.ErrorMessage) {
      setErrorDialogOpenForInProgress(true);
    } else throw new Error('Error saving headers');
  };

  const onContinue = async () => {
    if (currentConsignmentHasData && activeStep === 'AddingConsignment') {
      let responseDataAdd = await saveCurrentConsignment();
      if (responseDataAdd?.[0]?.ErrorMessage) {
        setErrorDialogOpenForInProgress(true);
      }
    }

    if (activeStep === 'UpdatingConsignment') {
      let responseDataUpdate = await saveCurrentConsignment();
      if (responseDataUpdate?.[0]?.ErrorMessage) {
        setErrorDialogOpenForInProgress(true);
      }
    } else if (activeStep === 'TransportAndJourney') onContinueTransportAndJourney();
    else if (activeStep === 'AdditionalDetails') {
      const dt = await PostAdditionalDetails({ headerId, AdditionalDetails: data['AdditionalDetails'] });
      if (dt?.[0]?.ErrorMessage) {
        setErrorDialogOpenForInProgress(true);
      }
      if (!dt?.[0]?.headerId && !dt?.[0]?.ErrorMessage) {
        throw new Error('Error saving headers');
      }
    }
    setFixOrContinueModalOpen(false);
    goToNextStepFunc();
    setGoToNextStepFunc(false);
  };

  //--------------------Show HMRC errors----------------------
  const handleClose = () => setErrorDialogOpen(false);
  const handleCloseForInProgress = () => {
    setErrorDialogOpenForInProgress(false);
    window.location.reload();
  };
  const showErrorModal = () => {
    try {
      setErrorDialogOpen(true);
      getErrorsDetails(state.headerId).then((data) => {
        setHMRCErrorData(data.data);
      });
    } catch (e) {
      console.error(e);
    }
  };

  //--------------------Get Consignment Details from XML----------------------

  const handleGetXMLDetails = (xmlData) => {
    const data = xmlData.Consignments;
    const e = { lines: [] };
    setCurrentConsignmentErrors(e);
    const e2 = { lines: [] };
    setCurrentConsignmentHMRCErrors(e2);
    handleGetMRNDetails(data);
  };

  //--------------------Clear selection from delete consignment modal----------------------
  useEffect(() => {
    setDeleteConsignmentRowsSelected(false);
  }, [deleteConsignmentModalOpen]);

  const providerValue = useMemo(
    () => ({
      headerId,
      setHeaderId,
      companyId,
      setCompanyId,
      navToConsignmentId,
      setNavToConsignmentId,
      isReadOnly,

      //Modals
      deleteConsignmentModalOpen,
      setDeleteConsignmentModalOpen,
      deleteConsignmentRowsSelected,
      setDeleteConsignmentRowsSelected,
      duplicateModalOpen,
      setDuplicateModalOpen,
      deleteShipmentModalOpen,
      setDeleteShipmentModalOpen,
      cannotDeleteShipmentModalOpen,
      setCannotDeleteShipmentModalOpen,
      submitModalOpen,
      setSubmitModalOpen,

      //Required to avoid circular dependency
      currentConsignmentErrors,
      currentConsignmentHMRCErrors,
      currentConsignment,
      setCurrentConsignment,

      //Reference Data
      referenceData,

      //Data
      data,
      setData,
      updateData,
      moveData,
      deleteData,

      //Errors
      errors,
      setErrors,
      errorsLocal,
      setErrorsLocal,
      errorsHMRC,
      checkErrors,
      checkErrorsStandard,
      checkErrorsConsignment,
      checkErrorsConsignmentLocal,
      hasErrors,
      handleShowWarningModal,
      allValid,
      isAccompanied,

      //Consignments
      addNewConsignment,
      editConsignment,
      updateCurrentConsignment,
      saveCurrentConsignment,
      moveConsignmentData,
      setBlankConsignmentData,
      selectNeenor,
      deleteConsignmentData,
      addNewConsignmentLine,
      updateCurrentLine,
      editConsignmentLine,
      saveConsignmentLine,
      deleteConsignmentLine,
      deleteConsignment,
      hasConsignments,
      currentLine,
    }),
    [
      headerId,
      setHeaderId,
      companyId,
      setCompanyId,
      navToConsignmentId,
      setNavToConsignmentId,

      //Modals
      deleteConsignmentModalOpen,
      setDeleteConsignmentModalOpen,
      deleteConsignmentRowsSelected,
      setDeleteConsignmentRowsSelected,
      duplicateModalOpen,
      setDuplicateModalOpen,
      deleteShipmentModalOpen,
      setDeleteShipmentModalOpen,
      cannotDeleteShipmentModalOpen,
      setCannotDeleteShipmentModalOpen,
      submitModalOpen,
      setSubmitModalOpen,

      //Required to avoid circular dependency
      currentConsignmentErrors,
      currentConsignmentHMRCErrors,
      currentConsignment,
      setCurrentConsignment,

      //Reference Data
      referenceData,

      //Data
      data,
      setData,
      updateData,
      moveData,
      deleteData,

      //Errors
      errors,
      setErrors,
      errorsLocal,
      setErrorsLocal,
      errorsHMRC,
      checkErrors,
      checkErrorsStandard,
      checkErrorsConsignmentLocal,
      hasErrors,
      handleShowWarningModal,
      allValid,
      isAccompanied,

      //Consignments
      addNewConsignment,
      editConsignment,
      updateCurrentConsignment,
      saveCurrentConsignment,
      moveConsignmentData,
      setBlankConsignmentData,
      selectNeenor,
      deleteConsignmentData,
      addNewConsignmentLine,
      updateCurrentLine,
      editConsignmentLine,
      saveConsignmentLine,
      deleteConsignmentLine,
      deleteConsignment,
      hasConsignments,
      currentLine,
    ],
    { deep: true }
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <GBENSContext.Provider value={providerValue}>
        <FixOrContinueModal
          open={FixOrContinueModalOpen}
          onFix={() => setFixOrContinueModalOpen(false)}
          onSave={onContinue}
        />

        {state?.image && (
          <div className="ENS-container">
            <div
              className="image-container"
              style={{ backgroundImage: `url('data:image/png;base64,${state.image}')` }}
            />
            <div className="header-companyId">
              {state?.currentCompany?.companyName} {state?.currentCompany?.companyEori}
              {isReadOnly && <>{READ_ONLY_LABEL}</>}
            </div>
          </div>
        )}

        {!state?.image && (
          <div className="header-companyId with-no-image">
            {state?.currentCompany?.companyName} {state?.currentCompany?.companyEori}
            {isReadOnly && <>{READ_ONLY_LABEL}</>}
          </div>
        )}

        <Stepper
          defaultStepKey={state?.activeStep || 'TransportAndJourney'}
          showFuture={true}
          canSkipForward={true}
          stepClassName="business-partner-section"
          useModalToChangeTabs={!isReadOnly}
          handleShowWarningModal={handleShowWarningModal}
          tabValidation={tabValidationErrors}
          hmrcTabValidation={HMRCtabValidationErrors}
        >
          <Container
            className="business-partner-container no-graphics"
            maxWidth="xl"
            sx={{
              p: 4,
              mt: 0,
              mb: 6,
            }}
          >
            <div>
              {state.error && (
                <div className="rowWrapper col-container shipment-view GB-container">
                  <Tabs
                    className="wrapper-tabs"
                    orientation="vertical"
                    value={menuErrorValue}
                    onChange={(_event, newValue) => setMenuErrorValue(newValue)}
                  >
                    <div className="shiipment-information-required">Shipment Action Required</div>
                    <Tab
                      className="no-uppercase"
                      label="View errors on this shipment"
                      {...a11yProps(0)}
                      onClick={showErrorModal}
                    />
                  </Tabs>
                </div>
              )}

              <Stepper.StepList
                isSticky={true}
                title={<div className="epo-steps-stepper-header">Create an ENS Declaration</div>}
              />
              <Stepper.ShowOnStep stepKey="ReviewAndSubmit">
                <ReviewAndSubmitMenu />
              </Stepper.ShowOnStep>
            </div>

            <Stepper.Step text="1. Transport and Journey" stepKey="TransportAndJourney">
              {!initialErrorCheckRun && state?.consignmentId ? (
                <div>
                  <LoadingSpinner />
                </div>
              ) : (
                <TransportAndJourney />
              )}
            </Stepper.Step>
            <Stepper.Step text="2. Consignments" stepKey="Consignments">
              <Consignments setConsignmentsVisited={setConsignmentsVisited} />
            </Stepper.Step>
            <Stepper.Step subStep text="Adding Consignment" stepKey="AddingConsignment">
              <AddingConsignment handleGetMRNDetails={handleGetMRNDetails} />
            </Stepper.Step>
            <Stepper.Step subStep text="Updating Consignment" stepKey="UpdatingConsignment">
              <UpdatingConsignments />
            </Stepper.Step>
            <Stepper.Step
              subStep
              text="Upload Consignment File"
              stepKey="uploadConsignment"
              showOnDifferentStep={['Consignments', 'AddingConsignment']}
            >
              <ConsignmentUpload handleGetXMLDetails={handleGetXMLDetails} headerId={headerId} companyId={companyId} />
            </Stepper.Step>
            <Stepper.Step text="3. Additional Details" stepKey="AdditionalDetails">
              <AdditionalDetails />
            </Stepper.Step>
            <Stepper.Step text="4. Review and Submit" stepKey="ReviewAndSubmit">
              <ReviewAndSubmit />
            </Stepper.Step>

            <Dialog
              open={errorDialogOpen}
              onClose={handleClose}
              className="ShipmentView"
              PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                  event.preventDefault();
                  handleClose();
                },
              }}
            >
              <DialogTitle>
                <span>
                  <WarningIcon style={{ color: 'red' }} />
                </span>{' '}
                HMRC Errors
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <p>Errors received on this shipment</p>
                  <TableContainer>
                    <Table
                      aria-label="simple table"
                      sx={{
                        [`& .${tableCellClasses.root}`]: {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Error</TableCell>
                          <TableCell align="right">Occurrence(s)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hmrcErrorData.map((row) => (
                          <TableRow key={row.errorCode}>
                            <TableCell component="th" scope="row">
                              {row.scenario}
                            </TableCell>
                            <TableCell align="right">{row.occurrenceCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <p>Please select the consignment to review and correct the error(s)</p>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={errorDialogOpenForInProgress}
              onClose={handleCloseForInProgress}
              className="ShipmentView"
              PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                  event.preventDefault();
                  handleCloseForInProgress();
                },
              }}
            >
              <DialogTitle>
                <span>
                  <WarningIcon style={{ color: 'red' }} />
                </span>{' '}
                HMRC Errors
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <p>This job is currently in progress and changes cannot be made.</p>
                  <p>Please try again once the job has finished submitting.</p>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseForInProgress}>Close</Button>
              </DialogActions>
            </Dialog>
          </Container>
        </Stepper>
      </GBENSContext.Provider>
    </LocalizationProvider>
  );
};

export default GBENSContainer;
